import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";

type AnimationData = Record<string, any>;

interface AnimationMap {
  [fileName: string]: AnimationData | null;
}

interface AnimationContextValue {
  animations: AnimationMap;
}

interface AnimationProviderProps {
  files: string[];
  children: ReactNode;
}

const AnimationContext = createContext<AnimationContextValue | null>(null);

export const useAnimationData = (fileName: string): AnimationData | null => {
  const context = useContext(AnimationContext);
  if (!context) {
    throw new Error(
      "useAnimationData must be used within an AnimationProvider"
    );
  }

  return context.animations[fileName] || null;
};

export const AnimationProvider = ({
  files,
  children,
}: AnimationProviderProps) => {
  const [animations, setAnimations] = useState<AnimationMap>({});

  useEffect(() => {
    if (!files || files.length === 0) return;

    const fetchAnimations = async () => {
      try {
        const fetchPromises = files.map(async (fileName) => {
          const path = `animations/lottie/${fileName}.json`;

          const res = await fetch(path);
          if (!res.ok) {
            throw new Error(`Failed to fetch ${path}`);
          }

          const data = await res.json();
          return { [fileName]: data };
        });

        const results = await Promise.all(fetchPromises);

        const merged = results.reduce((acc, curr) => {
          return { ...acc, ...curr };
        }, {} as AnimationMap);

        setAnimations(merged);
      } catch (error) {}
    };

    fetchAnimations();
  }, [files]);

  const value: AnimationContextValue = {
    animations,
  };

  return (
    <AnimationContext.Provider value={value}>
      {children}
    </AnimationContext.Provider>
  );
};
